import { Fragment, useMemo, useState } from "react";
import InputForm from "../inputs/InputForm";

/**
 * El formulario de a un paso por vez.
 *
 * POR QUÉ EXISTE. El de portería son ocho campos y se carga en el portón, con
 * el celular en una mano y un camión esperando en la otra. Entero en una sola
 * pantalla obliga a scrollear buscando cuál falta, y no hay forma de saber si
 * ya está listo para guardar sin recorrerlo todo de nuevo.
 *
 * Acá aparece una pregunta a la vez. Se contesta, "Siguiente", y la que sigue.
 * Los datos opcionales van todos juntos en un solo paso que se puede omitir, y
 * al final queda un resumen para revisar antes de guardar.
 *
 * NO ES UN FORMULARIO NUEVO. Usa los MISMOS campos y el MISMO InputForm que la
 * vista completa: los pasos salen de la metadata que ya existía —`principal`
 * es obligatorio, `secondary` es opcional, `checks` son los chequeos—, así que
 * agregar un campo lo agrega en las dos vistas sin tocar nada acá.
 */

const tieneValor = (v) =>
  v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && !v.length);

// Los ids no le dicen nada a nadie: para el resumen se busca la etiqueta en la
// misma lista de opciones que usa el desplegable.
const etiquetaDe = (campo, valor, opciones) => {
  if (campo.inputType === "boolean") {
    return valor ? campo.positive || "Sí" : campo.negative || "No";
  }
  const lista = opciones?.[campo.optionsList] || [];
  if (Array.isArray(valor)) {
    return (
      valor
        .map((v) => lista.find((o) => o.value === v)?.label ?? v)
        .join(", ") || null
    );
  }
  return lista.find((o) => o.value === valor)?.label ?? valor;
};

const FormPasos = ({
  campos = [],
  opciones = {},
  data,
  setData,
  elemento = null,
  onGuardar,
  onVerCompleto,
}) => {
  const [indice, setIndice] = useState(0);
  const modoEdicion = !!elemento;

  const handleChange = (key, value) =>
    setData((prev) => ({ ...prev, [key]: value }));

  const pasos = useMemo(() => {
    const principales = campos.filter((c) => c.type === "principal");
    const opcionales = campos.filter((c) => c.type === "secondary");
    const chequeos = campos.filter((c) => c.type === "checks");

    return [
      ...principales.map((campo) => ({
        clave: campo.key,
        titulo: campo.label,
        tipo: "campo",
        campo,
      })),
      ...(opcionales.length
        ? [
            {
              clave: "__otros",
              titulo: "Otros datos",
              tipo: "opcionales",
              campos: opcionales,
            },
          ]
        : []),
      ...(chequeos.length
        ? [
            {
              clave: "__chequeos",
              titulo: "Chequeos",
              tipo: "chequeos",
              campo: chequeos[0],
            },
          ]
        : []),
      { clave: "__resumen", titulo: "Revisar y guardar", tipo: "resumen" },
    ];
  }, [campos]);

  const paso = pasos[indice];
  const esUltimo = indice === pasos.length - 1;

  // Sólo frena si el campo es obligatorio. Un opcional vacío avanza igual.
  const puedeAvanzar =
    paso.tipo !== "campo" ||
    !paso.campo.important ||
    tieneValor(data[paso.campo.key]);

  const algoCargadoEnOpcionales =
    paso.tipo === "opcionales" &&
    paso.campos.some((c) => tieneValor(data[c.key]));

  const avanzar = () => {
    if (esUltimo) return onGuardar();
    if (puedeAvanzar) setIndice((i) => i + 1);
  };

  const camposCargados = campos.filter(
    (c) => c.type !== "checks" && tieneValor(data[c.key]),
  );

  const chequeosMarcados = campos
    .filter((c) => c.type === "checks")
    .flatMap((c) => c.items ?? [])
    .filter((item) => data.chequeos?.[item.key]);

  return (
    <div className="pasos">
      <div className="pasos-progreso">
        <div className="pasos-barra">
          <span style={{ width: `${((indice + 1) / pasos.length) * 100}%` }} />
        </div>
        <p className="pasos-cuenta">
          Paso {indice + 1} de {pasos.length}
        </p>
      </div>

      <h2 className="pasos-titulo">{paso.titulo}</h2>

      {/* `--un-campo` es lo que le dice a la hoja que el título de arriba ya
          nombra al campo. En «Otros datos» el título no nombra a ninguno, así
          que ahí cada campo lleva el suyo. */}
      <div
        className={`pasos-cuerpo${paso.tipo === "campo" ? " pasos-cuerpo--un-campo" : ""}`}
      >
        {paso.tipo === "campo" && (
          <InputForm
            campo={paso.campo}
            value={data[paso.campo.key]}
            onChange={handleChange}
            opciones={opciones}
            modoEdicion={modoEdicion}
          />
        )}

        {paso.tipo === "opcionales" && (
          <>
            <p className="pasos-nota">
              Nada de esto es obligatorio. Si no aplica, omitilo.
            </p>
            {paso.campos.map((campo) => (
              <Fragment key={campo.key}>
                {/* El sí/no es el único que `InputForm` dibuja sin nombre: en
                    la vista completa los dos botones se explican solos, pero
                    acá van entre una lista y un cuadro de texto. */}
                {campo.inputType === "boolean" && (
                  <strong className="pasos-etiqueta">{campo.label}</strong>
                )}
                <InputForm
                  campo={campo}
                  value={data[campo.key]}
                  onChange={handleChange}
                  opciones={opciones}
                  modoEdicion={modoEdicion}
                />
              </Fragment>
            ))}
          </>
        )}

        {paso.tipo === "chequeos" && (
          <div className="checkbox-list">
            {(paso.campo.items ?? []).map((item) => {
              const activo = data.chequeos?.[item.key] ?? false;
              return (
                <button
                  key={item.key}
                  type="button"
                  className={`boolean-btn ${activo ? "activo" : "inactivo"}`}
                  onClick={() =>
                    setData((prev) => ({
                      ...prev,
                      chequeos: { ...prev.chequeos, [item.key]: !activo },
                    }))
                  }
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        )}

        {paso.tipo === "resumen" && (
          <>
            {/* Cada línea vuelve a su paso. Darse cuenta de que se erró el
                chofer y tener que salir y volver a empezar sería peor que no
                tener resumen. */}
            <dl className="pasos-resumen">
              {camposCargados.map((campo) => (
                <div className="pasos-resumen-item" key={campo.key}>
                  <dt>{campo.label}</dt>
                  <dd>
                    <button
                      type="button"
                      className="pasos-volver"
                      onClick={() => {
                        const i = pasos.findIndex(
                          (p) =>
                            p.clave === campo.key ||
                            (p.tipo === "opcionales" &&
                              p.campos.includes(campo)),
                        );
                        if (i >= 0) setIndice(i);
                      }}
                    >
                      {etiquetaDe(campo, data[campo.key], opciones)}
                    </button>
                  </dd>
                </div>
              ))}
              {chequeosMarcados.length > 0 && (
                <div className="pasos-resumen-item">
                  <dt>Chequeos</dt>
                  <dd>{chequeosMarcados.map((i) => i.label).join(", ")}</dd>
                </div>
              )}
            </dl>
            {camposCargados.length === 0 && (
              <p className="pasos-nota">Todavía no hay nada cargado.</p>
            )}
          </>
        )}
      </div>

      <div className="pasos-botones">
        {indice > 0 ? (
          <button
            type="button"
            className="pasos-btn pasos-btn--atras"
            onClick={() => setIndice((i) => i - 1)}
          >
            Atrás
          </button>
        ) : (
          onVerCompleto && (
            <button
              type="button"
              className="pasos-btn pasos-btn--atras"
              onClick={onVerCompleto}
            >
              Ver todo
            </button>
          )
        )}

        <button
          type="button"
          className="pasos-btn pasos-btn--seguir"
          disabled={!puedeAvanzar}
          onClick={avanzar}
        >
          {/* El botón dice lo que va a pasar. En el paso opcional cambia solo
              según si se cargó algo: "Omitir" cuando está vacío, "Siguiente"
              cuando no, así no hay que elegir entre dos botones que hacen lo
              mismo. */}
          {esUltimo
            ? "Guardar"
            : paso.tipo === "opcionales" && !algoCargadoEnOpcionales
              ? "Omitir"
              : "Siguiente"}
        </button>
      </div>

      {!puedeAvanzar && (
        <p className="pasos-falta">Este dato es obligatorio para continuar.</p>
      )}
    </div>
  );
};

export default FormPasos;
